"use client";

import { useSoftwarePagamentosPage } from "./hooks/use-software-pagamentos-page";
import { useSeo } from "@/features/shared/lib/use-seo";
import {
  HeroSection,
  MeiosPagamentoSection,
  VantagensSection,
  PorQueSection,
} from "./components";
import { GenericInfoSection } from "@/features/shared/components/generic-info-section";
import { INFO_SECTIONS } from "@/features/shared/content/info-sections";

// Provas Sociais importadas da Home e UI
import { NumerosSection } from "@/features/home/components/numeros-section";
import { NaMidiaSection } from "@/features/home/components/na-midia-section";
import { SoftwareProductsCarousel, TrustedLogosMarquee, WallOfLoveSection, DorParallaxSection } from "@/features/shared/components";
import { FAQAccordion } from "@/features/ui/components/faq-accordion";
import { LeadCaptureCTA } from "@/features/ui/components/lead-capture-cta";
import { useLeadCapture } from "@/features/shared/lib/lead-capture-context";
import { UltimasDoBlogSection } from "@/features/shared/components/ultimas-do-blog-section-lazy";
import { getProdutoIcone } from "@/features/shared/data/produtos-data";

import { Spinner } from "@/components/ui/spinner";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent } from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

import { artigosMidia, depoimentos, numeros, videosData } from "@/features/shared/data/social-proof-data";

const LEAD_TITLE = "Software de Pagamentos";

function SoftwarePagamentosPage() {
  useSeo({
    title: "Software de Pagamentos para Hotéis | Foco Tecnologia",
    description:
      "Processe pagamentos de reservas com segurança: múltiplas formas de pagamento, split automático, conciliação e conformidade com PCI DSS.",
    path: "/software-de-pagamentos",
  });

  const { openLeadCapture } = useLeadCapture();
  const { data, isLoading, isError, refetch } = useSoftwarePagamentosPage();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="w-12 h-12" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Empty className="text-center">
          <EmptyHeader>
            <EmptyTitle>Erro ao carregar dados</EmptyTitle>
            <EmptyDescription>
              Não foi possível carregar o conteúdo da página. Tente novamente.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={() => refetch()} variant="outline" className="rounded-full px-6">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar novamente
            </Button>
          </EmptyContent>
        </Empty>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Section 1: Hero */}
      <HeroSection
        data={data.hero}
        onCtaClick={() => openLeadCapture({ source: "form-software-de-pagamentos", title: LEAD_TITLE, icon: getProdutoIcone("/software-de-pagamentos") })}
      />

      {/* Section 2: O que é o Foco Pay */}
      <GenericInfoSection
        {...INFO_SECTIONS.softwarePagamentos}
        imageSide="right"
        showMockup
        desktopMockupSrc="/assets/imgs/software-de-pagamento/transacoes.webp"
      />

      {/* Diagnóstico: prejuízos na gestão (mesmo cenário da home) */}
      <DorParallaxSection
        dorId="prejuizos"
        backgroundImage="/assets/imgs/gestao-hoteleira-pms/dor.webp"
        backgroundAlt="Hoteleiro estressado diante de um relatório financeiro com prejuízo operacional"
      />

      {/* Section 3: Meios de Pagamento */}
      <MeiosPagamentoSection />

      {/* Section 4: Por que automatizar os pagamentos */}
      <PorQueSection />

      {/* Section 5: Vantagens do FocoPay */}
      <VantagensSection />

      {/* Prova Social: Mural de depoimentos (texto + vídeo, unificados) */}
      <WallOfLoveSection
        depoimentos={depoimentos}
        videos={videosData}
        badge="Depoimentos"
        title="Um mural de resultados reais"
        subtitle="Cada card aqui é um hoteleiro de verdade, contando como a Foco automatizou seus pagamentos."
      />

      {/* Prova Social: Na Mídia */}
      <NaMidiaSection artigos={artigosMidia} />

      {/* Bloco de Autoridade e Confiança */}
      <NumerosSection numeros={numeros} />
      <TrustedLogosMarquee />

      {/* Prova Social: Carrossel de Produtos */}
      <SoftwareProductsCarousel />

      <UltimasDoBlogSection productSlug="software-de-pagamentos" />

      {/* Prova Social: FAQ */}
      <FAQAccordion
        items={[
          { id: "1", question: "O que é o Foco Pay?", answer: "O Foco Pay é um software de pagamentos para hotéis que automatiza a gestão financeira, integra múltiplos gateways de pagamento e garante segurança e rapidez em cada transação." },
          { id: "2", question: "Quais gateways de pagamento são integrados?", answer: "O Foco Pay integra com Sicredi, PagSeguro, Sip2g, Cielo e outros principais gateways do mercado brasileiro, oferecendo flexibilidade total para seus hóspedes." },
          { id: "3", question: "A plataforma é segura?", answer: "Sim! O Foco Pay segue os mais altos padrões de segurança do mercado, com criptografia de dados, conformidade com PCI DSS e proteção contra fraudes." },
          { id: "4", question: "Como funciona a conciliação automática?", answer: "O sistema concilia automaticamente todas as transações, eliminando erros manuais e garantindo que o fluxo de caixa seja sempre preciso e confiável." },
          { id: "5", question: "Posso acompanhar as transações em tempo real?", answer: "Sim! O dashboard do Foco Pay permite acompanhar todas as transações em tempo real, com relatórios detalhados e alertas de segurança." },
        ]}
        title="Dúvidas Frequentes"
        subtitle="Tire suas dúvidas sobre o Foco Pay"
        badge="FAQ"
        showContactButton
      />

      {/* Lead Capture CTA */}
      <LeadCaptureCTA
        title="Pronto para automatizar a gestão financeira do seu hotel?"
        subtitle="Solicite uma demonstração e descubra como o Foco Pay pode transformar seus pagamentos."
        badge="Comece agora"
        source="software-de-pagamentos"
        leadTitle={LEAD_TITLE}
      />
    </div>
  );
}

export { SoftwarePagamentosPage };
